/**
 * Admin V3 — Booking Detail page (AV3-M1)
 *
 * V3-native booking drill-down. Mounted at /admin-v3/bookings/:bookingId
 * (replaces the Phase-1 DetailPlaceholder). Eliminates the operational
 * dependency on the legacy V2 BookingDetailPage for NON-FINANCIAL operations:
 *   - Booking detail (route / customer / pricing / driver / payment / status)
 *   - Enriched event timeline (incl. read-only REFUND_PROCESSED history) + risk
 *   - Read-only incidents already carried by the detail payload
 *   - Cancel booking      → POST /admin/control-tower/bookings/{id}/cancel
 *   - Reassign driver     → POST /admin/control-tower/bookings/{id}/reassign
 *   - Refund (A-2)        → POST /payments/refund (RBAC payment:refund)
 *   - Refund history (A-2)→ GET  /payments/refund/history/{id} (RBAC payment:refund:history, read-only)
 *   - Incident create (A-4)→ POST  /admin/control-tower/incidents (RBAC admin:bookings:incident)
 *   - Incident status (A-4)→ PATCH /admin/control-tower/incidents/{id} (RBAC admin:bookings:incident)
 *
 * Source: GET /admin/control-tower/bookings/{id}/detail (RBAC admin:bookings:list)
 *         → { booking, driver, payment, timeline, incidents, risk }
 *         GET /admin/control-tower/enums (cancel/reassign/incident vocab + status transitions)
 *
 * Refund history is ALSO surfaced READ-ONLY via the timeline's REFUND_PROCESSED events.
 *
 * RBAC: mutation affordances hidden unless the operator holds the matching
 * permission (HIDE posture). Backend @rbac_permission stays authoritative.
 * No backend changes. V2 BookingDetailPage remains fully operational.
 */
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, MapPin, User, Euro, Car, CreditCard, History,
  AlertTriangle, ShieldAlert, Ban, Users, Loader2, RotateCcw, Receipt,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  cn, currency, formatAddressLines, bookingStatusMeta, timelineEventLabels,
  driverStatusMeta, SmallPill, EmptyState,
} from '../../shared/visualTokens';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import usePermissions from '../../shared/hooks/usePermissions';
import { t, DEFAULT_LOCALE } from '../../i18n';
import BookingCancelModal from './BookingCancelModal';
import BookingReassignModal from './BookingReassignModal';
import BookingRefundModal from './BookingRefundModal';
import BookingRefundHistoryModal from './BookingRefundHistoryModal';
import BookingIncidentCreateModal from './BookingIncidentCreateModal';
import BookingIncidentUpdateModal from './BookingIncidentUpdateModal';
import RefundReconciliationPanel from './RefundReconciliationPanel';

const PERM_CANCEL = 'admin:bookings:cancel';
const PERM_REASSIGN = 'admin:bookings:assign';
const PERM_REFUND = 'payment:refund';
const PERM_REFUND_HISTORY = 'payment:refund:history';
const PERM_INCIDENT = 'admin:bookings:incident';

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    }).format(typeof iso === 'string' ? new Date(iso) : iso);
  } catch { return String(iso); }
};

// FC-R2 T8 (SPEC-8): the persisted booking.payment_status is authoritative
// (written by the canonical capture unit since FC-R2). Known values render a
// localized label; unknown values keep rendering raw and missing keeps the '—'
// fallback (pre-T8 behaviour, no crash). DISPLAY-ONLY — refund eligibility and
// history remain keyed to the backend-derived refund_eligible/payment_present
// (FC-R1), never to this label.
const PAYMENT_STATUS_LABEL_KEYS = {
  paid: 'bookings.detail.paymentStatus.paid',
  partially_refunded: 'bookings.detail.paymentStatus.partiallyRefunded',
  refunded: 'bookings.detail.paymentStatus.refunded',
};

const paymentStatusLabel = (status, locale) => (
  PAYMENT_STATUS_LABEL_KEYS[status]
    ? t(PAYMENT_STATUS_LABEL_KEYS[status], locale)
    : (status || '—')
);

const Panel = ({ title, icon: Icon, badge, children }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4">
    <div className="mb-3 flex items-center gap-2">
      {Icon && <Icon className="h-4 w-4 text-slate-500" />}
      <h3 className="text-[13px] font-semibold text-slate-900">{title}</h3>
      {badge && (
        <span className="ml-auto rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700 border border-amber-200">
          {badge}
        </span>
      )}
    </div>
    {children}
  </div>
);

const Row = ({ label, children, mono }) => (
  <div className="flex items-start justify-between gap-3 text-[13px]">
    <span className="text-slate-400">{label}</span>
    <span className={cn('text-right text-slate-800', mono && 'font-mono text-[12px]')}>{children}</span>
  </div>
);

const BookingDetailPage = () => {
  const locale = DEFAULT_LOCALE;
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { can, loading: permsLoading } = usePermissions();

  const { data, loading, error, refetch } = useAdminQuery(
    bookingId ? `/admin/control-tower/bookings/${bookingId}/detail` : null,
  );
  const { data: enumsData } = useAdminQuery('/admin/control-tower/enums');

  const [cancelOpen, setCancelOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundHistoryOpen, setRefundHistoryOpen] = useState(false);
  const [incidentCreateOpen, setIncidentCreateOpen] = useState(false);
  const [incidentUpdate, setIncidentUpdate] = useState(null);

  const booking = data?.booking || null;
  const driver = data?.driver || null;
  const payment = data?.payment || null;
  const timeline = Array.isArray(data?.timeline) ? data.timeline : [];
  const incidents = Array.isArray(data?.incidents) ? data.incidents : [];
  const risk = data?.risk || null;

  const statusMeta = booking ? bookingStatusMeta[booking.status] : null;
  const canModify = booking && booking.status !== 'cancelled' && booking.status !== 'completed';
  // Refund availability is independent of canModify: a completed booking can still be
  // refunded. FC-R1: gate on the backend-derived `refund_eligible` — computed from the
  // canonical source the refund authority itself trusts (booking.payment_provider +
  // payment_id), NOT the `payments` collection, which the B2C Stripe flow never writes
  // (so `payment` is null for genuinely-paid bookings). `isRefunded` is kept as
  // defence-in-depth; the backend remains authoritative (over-remaining / already-
  // refunded / duplicate-idempotency).
  const isRefunded = Boolean(booking) && booking.status === 'refunded';
  const canRefund = Boolean(data?.refund_eligible) && !isRefunded;
  const cancellationReasons = enumsData?.cancellation_reasons || [];
  const reassignReasons = enumsData?.reassign_reasons || [];
  const incidentTypes = enumsData?.incident_types || [];
  const incidentStatusTransitions = enumsData?.incident_status_transitions || {};

  const back = (
    <Button variant="ghost" size="sm" onClick={() => navigate('/admin/bookings')} data-testid="adminv3-booking-detail-back">
      <ArrowLeft className="h-4 w-4 mr-1.5" />{t('bookings.detail.back', locale)}
    </Button>
  );

  return (
    <div className="p-6 space-y-5" data-testid="adminv3-booking-detail-page">
      <div className="flex items-center justify-between">
        {back}
        {statusMeta && <SmallPill meta={statusMeta} />}
      </div>

      {loading && !data && (
        <div className="flex items-center justify-center gap-2 py-12 text-sm text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" />{t('bookings.detail.loading', locale)}
        </div>
      )}
      {error && !loading && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700" data-testid="adminv3-booking-detail-error">
          {error}
        </div>
      )}

      {booking && (
        <>
          {/* Header: reference + status + actions */}
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-[26px] font-semibold text-slate-900">
                {t('bookings.detail.title', locale).replace('{ref}', booking.booking_reference || bookingId)}
              </h1>
              <p className="text-[12px] text-slate-400">
                {t('bookings.detail.created', locale).replace('{date}', fmtDateTime(booking.created_at))}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {permsLoading && (
                <span className="flex items-center px-2 text-slate-300" data-testid="adminv3-booking-detail-perms-loading">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                </span>
              )}
              {canModify && can(PERM_REASSIGN) && (
                <Button
                  variant={driver ? 'outline' : 'default'}
                  size="sm"
                  onClick={() => setReassignOpen(true)}
                  data-testid={driver ? 'adminv3-booking-reassign-action' : 'adminv3-booking-assign-action'}
                >
                  <Users className="h-3.5 w-3.5 mr-1.5" />
                  {driver
                    ? t('bookings.detail.action.reassign', locale)
                    : t('bookings.detail.action.assign', locale)}
                </Button>
              )}
              {canModify && can(PERM_CANCEL) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCancelOpen(true)}
                  className="border-rose-200 text-rose-700 hover:bg-rose-50"
                  data-testid="adminv3-booking-cancel-action"
                >
                  <Ban className="h-3.5 w-3.5 mr-1.5" />{t('bookings.detail.action.cancel', locale)}
                </Button>
              )}
              {can(PERM_REFUND) && canRefund && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRefundOpen(true)}
                  className="border-amber-200 text-amber-700 hover:bg-amber-50"
                  data-testid="adminv3-booking-refund-action"
                >
                  <RotateCcw className="h-3.5 w-3.5 mr-1.5" />{t('bookings.detail.action.refund', locale)}
                </Button>
              )}
              {can(PERM_REFUND_HISTORY) && data?.payment_present && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setRefundHistoryOpen(true)}
                  data-testid="adminv3-booking-refund-history-action"
                >
                  <Receipt className="h-3.5 w-3.5 mr-1.5" />{t('bookings.detail.action.refundHistory', locale)}
                </Button>
              )}
              {can(PERM_INCIDENT) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIncidentCreateOpen(true)}
                  className="border-amber-200 text-amber-700 hover:bg-amber-50"
                  data-testid="adminv3-booking-incident-action"
                >
                  <AlertTriangle className="h-3.5 w-3.5 mr-1.5" />{t('bookings.detail.action.incident', locale)}
                </Button>
              )}
            </div>
          </div>

          {/* Primary info grid */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Panel title={t('bookings.detail.section.route', locale)} icon={MapPin}>
              <div className="space-y-2">
                <Row label={t('bookings.detail.field.pickup', locale)}>
                  <span className="whitespace-pre-line">{formatAddressLines(booking.pickup_address) || '—'}</span>
                </Row>
                <Row label={t('bookings.detail.field.destination', locale)}>
                  <span className="whitespace-pre-line">{formatAddressLines(booking.destination_address) || '—'}</span>
                </Row>
                <Row label={t('bookings.detail.field.date', locale)}>{booking.date || '—'}</Row>
                <Row label={t('bookings.detail.field.time', locale)}>{booking.time || '—'}</Row>
                <Row label={t('bookings.detail.field.passengers', locale)}>{booking.passengers ?? '—'}</Row>
              </div>
            </Panel>

            <Panel title={t('bookings.detail.section.customer', locale)} icon={User}>
              <div className="space-y-2">
                <Row label={t('bookings.detail.field.email', locale)}>{booking.email || '—'}</Row>
                <Row label={t('bookings.detail.field.phone', locale)}>{booking.phone || '—'}</Row>
                {booking.notes && <Row label={t('bookings.detail.field.notes', locale)}>{booking.notes}</Row>}
              </div>
            </Panel>

            <Panel title={t('bookings.detail.section.pricing', locale)} icon={Euro} badge={t('bookings.detail.readOnly', locale)}>
              <div className="space-y-2">
                <Row label={t('bookings.detail.field.serviceType', locale)}>
                  <span className="uppercase">{booking.pricing?.service_type || '—'}</span>
                </Row>
                <Row label={t('bookings.detail.field.distance', locale)}>
                  {booking.pricing?.distance_km != null ? `${Number(booking.pricing.distance_km).toFixed(2)} km` : '—'}
                </Row>
                <Row label={t('bookings.detail.field.duration', locale)}>
                  {booking.pricing?.duration_minutes != null ? `${booking.pricing.duration_minutes} min` : '—'}
                </Row>
                {booking.pricing?.surge_multiplier > 1 && (
                  <Row label={t('bookings.detail.field.surge', locale)}>x{booking.pricing.surge_multiplier}</Row>
                )}
                <div className="flex items-center justify-between border-t border-slate-100 pt-2 text-[13px]">
                  <span className="font-medium text-slate-500">{t('bookings.detail.field.total', locale)}</span>
                  <span className="text-[16px] font-bold text-slate-900">
                    {currency(booking.pricing?.final_price ?? booking.pricing?.estimated_price ?? 0)}
                  </span>
                </div>
              </div>
            </Panel>
          </div>

          {/* Driver + payment */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Panel title={t('bookings.detail.section.driver', locale)} icon={Car}>
              {driver ? (
                <div className="space-y-2">
                  <Row label={t('bookings.detail.field.name', locale)}>{driver.name || '—'}</Row>
                  <Row label={t('bookings.detail.field.email', locale)}>{driver.email || '—'}</Row>
                  <Row label={t('bookings.detail.field.phone', locale)}>{driver.phone || '—'}</Row>
                  <Row label={t('bookings.detail.field.licensePlate', locale)} mono>{driver.license_plate || '—'}</Row>
                  <div className="flex items-center justify-between text-[13px]">
                    <span className="text-slate-400">{t('bookings.detail.field.status', locale)}</span>
                    <SmallPill meta={driverStatusMeta[driver.status] || { label: driver.status, pill: 'bg-slate-100 text-slate-500 border border-slate-200' }} />
                  </div>
                </div>
              ) : (
                <EmptyState icon={Users} title={t('bookings.detail.driver.none', locale)} />
              )}
            </Panel>

            <Panel title={t('bookings.detail.section.payment', locale)} icon={CreditCard}>
              {payment ? (
                <div className="space-y-2">
                  <Row label={t('bookings.detail.field.provider', locale)}>
                    <span className="uppercase">{payment.provider || '—'}</span>
                  </Row>
                  <Row label={t('bookings.detail.field.status', locale)}>{payment.status || '—'}</Row>
                  <Row label={t('bookings.detail.field.amount', locale)}>
                    {payment.amount != null ? `${Number(payment.amount).toFixed(2)} ${(payment.currency || 'EUR').toUpperCase()}` : '—'}
                  </Row>
                  <Row label={t('bookings.detail.field.txnId', locale)} mono>{payment.transaction_id || '—'}</Row>
                </div>
              ) : (
                <Row label={t('bookings.detail.field.status', locale)}>
                  <span data-testid="adminv3-booking-payment-status">
                    {paymentStatusLabel(booking.payment_status, locale)}
                  </span>
                </Row>
              )}
            </Panel>
          </div>

          {/* Refund / avoir / correction-chain reconciliation (RU-9, read-only) */}
          <RefundReconciliationPanel bookingId={bookingId} locale={locale} />

          {/* Risk (read-only) */}
          {risk && (
            <Panel title={t('bookings.detail.section.risk', locale)} icon={ShieldAlert} badge={t('bookings.detail.readOnly', locale)}>
              <div className="flex flex-wrap items-center gap-3 text-[13px]">
                <SmallPill
                  meta={{
                    label: `${t('bookings.detail.risk.score', locale)}: ${risk.risk_score} (${risk.risk_level})`,
                    pill: risk.risk_level === 'low'
                      ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                      : risk.risk_level === 'medium'
                        ? 'bg-amber-100 text-amber-700 border border-amber-200'
                        : 'bg-rose-100 text-rose-700 border border-rose-200',
                  }}
                />
                {Array.isArray(risk.signals) && risk.signals.length > 0 && (
                  <span className="text-slate-500">{t('bookings.detail.risk.signals', locale)}: {risk.signals.join(', ')}</span>
                )}
              </div>
            </Panel>
          )}

          {/* Incidents (read-only — management stays in V2 / AV3-M2) */}
          {incidents.length > 0 && (
            <Panel title={`${t('bookings.detail.section.incidents', locale)} (${incidents.length})`} icon={AlertTriangle}>
              <div className="space-y-2">
                {incidents.map((inc, idx) => (
                  <div key={idx} className="rounded-lg border border-rose-100 bg-rose-50/50 p-3 text-[13px]" data-testid="adminv3-booking-incident-row">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <span className="font-medium capitalize text-slate-900">{(inc.type || '').replace(/_/g, ' ')}</span>
                        {inc.description && <span className="text-slate-500"> — {inc.description}</span>}
                      </div>
                      {inc.status && <span className="text-[11px] font-semibold text-rose-700">{inc.status}</span>}
                    </div>
                    {(inc.reported_by || inc.reported_at) && (
                      <div className="mt-1 text-[11px] text-slate-400">
                        {inc.reported_by || '—'} · {fmtDateTime(inc.reported_at)}
                      </div>
                    )}
                    {can(PERM_INCIDENT) && inc.id && (
                      <div className="mt-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-[12px]"
                          onClick={() => setIncidentUpdate(inc)}
                          data-testid="adminv3-booking-incident-update-action"
                        >
                          {t('bookings.detail.action.incidentUpdate', locale)}
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Panel>
          )}

          {/* Timeline (enriched, incl. read-only refund history via REFUND_PROCESSED) */}
          <Panel title={t('bookings.detail.section.timeline', locale)} icon={History}>
            {timeline.length > 0 ? (
              <div className="space-y-3">
                {timeline.map((ev, idx) => {
                  const meta = timelineEventLabels[ev.event];
                  return (
                    <div key={idx} className="flex items-start gap-3 text-[13px]" data-testid="adminv3-booking-timeline-row">
                      <div className={cn('mt-1.5 h-2 w-2 flex-shrink-0 rounded-full', meta?.dot || 'bg-slate-300')} />
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={cn('font-medium', meta?.color || 'text-slate-800')}>{meta?.label || ev.event}</span>
                          {ev.actor_type && (
                            <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                              {ev.actor_type}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-400">
                          {fmtDateTime(ev.timestamp)}{ev.actor ? ` · ${ev.actor}` : ''}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyState icon={History} title={t('bookings.detail.timeline.empty', locale)} />
            )}
          </Panel>
        </>
      )}

      <BookingCancelModal
        open={cancelOpen}
        bookingId={bookingId}
        booking={booking}
        reasons={cancellationReasons}
        onClose={() => setCancelOpen(false)}
        onDone={refetch}
        locale={locale}
      />
      <BookingReassignModal
        open={reassignOpen}
        bookingId={bookingId}
        booking={booking}
        reasons={reassignReasons}
        mode={driver ? 'reassign' : 'assign'}
        onClose={() => setReassignOpen(false)}
        onDone={refetch}
        locale={locale}
      />
      <BookingRefundModal
        open={refundOpen}
        bookingId={bookingId}
        booking={booking}
        payment={payment}
        onClose={() => setRefundOpen(false)}
        onDone={refetch}
        locale={locale}
      />
      <BookingRefundHistoryModal
        open={refundHistoryOpen}
        bookingId={bookingId}
        onClose={() => setRefundHistoryOpen(false)}
        locale={locale}
      />
      <BookingIncidentCreateModal
        open={incidentCreateOpen}
        bookingId={bookingId}
        booking={booking}
        incidentTypes={incidentTypes}
        onClose={() => setIncidentCreateOpen(false)}
        onDone={refetch}
        locale={locale}
      />
      <BookingIncidentUpdateModal
        open={Boolean(incidentUpdate)}
        incident={incidentUpdate}
        statusTransitions={incidentStatusTransitions}
        onClose={() => setIncidentUpdate(null)}
        onDone={refetch}
        locale={locale}
      />
    </div>
  );
};

export default BookingDetailPage;
